import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type DensityMode = 'comfortable' | 'compact';

export interface PersistedUIState {
  sidebarCollapsed: boolean;
  groupCollapsed: Record<string, boolean>;
  favorites: string[];
  theme: ThemeMode;
  density: DensityMode;
  activeWorkspace: string;
  activeDepartment: string;
  /**
   * BizPlus parity (Phase 0.3) — the branch the shell is currently scoped to.
   * `'all'` means unrestricted (matches branchFilterFragment on the backend,
   * src/shared/branch/resolve.js); otherwise a BusinessLocation id. This is a
   * transient VIEW filter, distinct from User.default_branch_id (the person's
   * admin-assigned home branch, used when they CREATE a document) — a
   * multi-branch manager can browse branch B's dashboard while their own home
   * branch stays A.
   */
  activeBranchId: string;
}

export interface UIState extends PersistedUIState {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setGroupCollapsed: (groupId: string, collapsed: boolean) => void;
  toggleGroupCollapsed: (groupId: string) => void;
  setFavorites: (routes: string[]) => boolean;
  addFavorite: (route: string) => boolean;
  removeFavorite: (route: string) => void;
  setTheme: (theme: ThemeMode) => void;
  setDensity: (density: DensityMode) => void;
  setActiveWorkspace: (workspace: string) => void;
  setActiveDepartment: (department: string) => void;
  setActiveBranchId: (branchId: string) => void;
}

export const UI_PREFERENCE_STORAGE_KEY = 'leadflow-crm:ui-preferences';
export const MAX_FAVORITES = 20;

const defaultPreferences = (): PersistedUIState => ({
  sidebarCollapsed: false,
  groupCollapsed: {},
  favorites: [],
  // ZYNX is a light-first product: many screens have no `dark:` variants, so
  // inheriting a dark OS preference rendered them unreadable (light-on-light).
  // Default to light; 'dark'/'system' remain available as explicit choices.
  theme: 'light',
  density: 'comfortable',
  activeWorkspace: 'me',
  activeDepartment: 'mine',
  activeBranchId: 'all',
});

const memoryStorage = (): StateStorage => {
  const values = new Map<string, string>();
  return {
    getItem: (name) => values.get(name) ?? null,
    setItem: (name, value) => void values.set(name, value),
    removeItem: (name) => void values.delete(name),
  };
};

const fallbackStorage = memoryStorage();
const storage = createJSONStorage<PersistedUIState>(() =>
  typeof window === 'undefined' ? fallbackStorage : window.localStorage,
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizedValue = (value: unknown, fallback: string, maxLength = 256) => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim();
  return normalized && normalized.length <= maxLength ? normalized : fallback;
};

const isObjectIdLike = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{24}$/i.test(value);

const normalizedList = (value: unknown, limit: number) => {
  if (!Array.isArray(value)) return [];
  const unique = new Set<string>();
  for (const item of value) {
    const normalized = normalizedValue(item, '');
    if (normalized) unique.add(normalized);
    if (unique.size === limit) break;
  }
  return [...unique];
};

const normalizePreferences = (value: unknown): PersistedUIState => {
  const defaults = defaultPreferences();
  if (!isRecord(value)) return defaults;
  const groupCollapsed: Record<string, boolean> = {};
  if (isRecord(value.groupCollapsed)) {
    Object.entries(value.groupCollapsed)
      .slice(0, 100)
      .forEach(([key, collapsed]) => {
        const groupId = normalizedValue(key, '', 100);
        if (groupId && typeof collapsed === 'boolean') groupCollapsed[groupId] = collapsed;
      });
  }
  return {
    sidebarCollapsed:
      typeof value.sidebarCollapsed === 'boolean' ? value.sidebarCollapsed : defaults.sidebarCollapsed,
    groupCollapsed,
    favorites: normalizedList(value.favorites, MAX_FAVORITES),
    // 'system' was the old default, never a deliberate choice, and it silently
    // pulled dark-OS users into an unreadable half-built dark theme. Migrate it
    // to light. An explicit 'dark' selection is respected.
    theme:
      value.theme === 'light' || value.theme === 'dark'
        ? value.theme
        : defaults.theme,
    density:
      value.density === 'comfortable' || value.density === 'compact'
        ? value.density
        : defaults.density,
    activeWorkspace: normalizedValue(value.activeWorkspace, defaults.activeWorkspace, 128),
    activeDepartment: normalizedValue(value.activeDepartment, defaults.activeDepartment, 128),
    activeBranchId:
      value.activeBranchId === 'all' || isObjectIdLike(value.activeBranchId)
        ? (value.activeBranchId as string)
        : defaults.activeBranchId,
  };
};

export function applyAppearance(theme: ThemeMode, density: DensityMode) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const systemDark =
    theme === 'system' &&
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || systemDark;
  root.dataset.theme = theme;
  root.dataset.density = density;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  root.style.setProperty('--shell-density-scale', density === 'compact' ? '0.875' : '1');
}

/** Pure UI preferences only. Server data belongs in TanStack Query. */
export const useUIStore = create<UIState>()(
  persist<UIState, [], [], PersistedUIState>(
    (set, get) => ({
      ...defaultPreferences(),
      sidebarOpen: false,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebarCollapsed: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setGroupCollapsed: (groupId, collapsed) => {
        const normalized = normalizedValue(groupId, '', 100);
        if (normalized) {
          set((state) => ({
            groupCollapsed: { ...state.groupCollapsed, [normalized]: collapsed },
          }));
        }
      },
      toggleGroupCollapsed: (groupId) => {
        const normalized = normalizedValue(groupId, '', 100);
        if (normalized) {
          set((state) => ({
            groupCollapsed: {
              ...state.groupCollapsed,
              [normalized]: !state.groupCollapsed[normalized],
            },
          }));
        }
      },
      setFavorites: (routes) => {
        const normalized = normalizedList(routes, MAX_FAVORITES + 1);
        if (normalized.length > MAX_FAVORITES) return false;
        set({ favorites: normalized });
        return true;
      },
      addFavorite: (route) => {
        const normalized = normalizedValue(route, '');
        const favorites = get().favorites;
        if (!normalized || favorites.includes(normalized) || favorites.length >= MAX_FAVORITES) {
          return false;
        }
        set({ favorites: [...favorites, normalized] });
        return true;
      },
      removeFavorite: (route) =>
        set((state) => ({ favorites: state.favorites.filter((item) => item !== route) })),
      setTheme: (theme) => {
        const normalized = normalizePreferences({ theme }).theme;
        set({ theme: normalized });
        applyAppearance(normalized, get().density);
      },
      setDensity: (density) => {
        const normalized = normalizePreferences({ density }).density;
        set({ density: normalized });
        applyAppearance(get().theme, normalized);
      },
      setActiveWorkspace: (workspace) =>
        set((state) => ({
          activeWorkspace: normalizedValue(workspace, state.activeWorkspace, 128),
        })),
      setActiveDepartment: (department) =>
        set((state) => ({
          activeDepartment: normalizedValue(department, state.activeDepartment, 128),
        })),
      setActiveBranchId: (branchId) =>
        set((state) => ({
          activeBranchId:
            branchId === 'all' || isObjectIdLike(branchId) ? branchId : state.activeBranchId,
        })),
    }),
    {
      name: UI_PREFERENCE_STORAGE_KEY,
      version: 1,
      storage,
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        groupCollapsed: state.groupCollapsed,
        favorites: state.favorites,
        theme: state.theme,
        density: state.density,
        activeWorkspace: state.activeWorkspace,
        activeDepartment: state.activeDepartment,
        activeBranchId: state.activeBranchId,
      }),
      migrate: (persisted) => normalizePreferences(persisted),
      merge: (persisted, current) => ({ ...current, ...normalizePreferences(persisted) }),
      onRehydrateStorage: () => (state) => {
        if (state) applyAppearance(state.theme, state.density);
      },
    },
  ),
);

applyAppearance(useUIStore.getState().theme, useUIStore.getState().density);

if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme, density } = useUIStore.getState();
    if (theme === 'system') applyAppearance(theme, density);
  });
}
